import { FC } from 'react';
import { useTranslation } from 'next-i18next';
import {
  EXTERNAL_PATHS,
  NEW_TAB_ATTRIBUTES,
  NEXT_STATIC_IMAGES,
  SHARED_MEDIA_QUERIES as sharedMediaQueries,
} from 'src/common/constants';

import PitchPageLayout from 'src/common/components/PitchPageLayout';
import Typography from 'src/common/components/Typography';
import Button from 'src/common/components/ButtonDS22';
import { buildSrcSet } from 'src/common/utils/imageHelpers';
import styles from './OwnerAndCaptainGuides.module.scss';

const sections = [
  { id: 0, link: `${EXTERNAL_PATHS.JOURNAL}how-to` },
];

const imageWidths = {
  s: 335,
  m: 448,
  l: 380,
  xl: 448,
};

const buildUrl = (sectionIndex: number, variant: 's' | 'm' | 'l' | 'xl') => {
  const src = `${NEXT_STATIC_IMAGES}safety/safety--${sectionIndex}--${variant}.jpg`;
  return buildSrcSet(src, { w: imageWidths[variant] });
};

const OwnerAndCaptainGuides: FC = () => {
  const [t] = useTranslation('safety-page');

  return (
    <div className={styles.root}>
      <PitchPageLayout backgroundColor="pearl">
        <div className={styles.main}>
          {sections.map((section) => (
            <section key={section.id} className={styles.section}>
              <div className={styles.contentWrapper}>
                <Typography type="p" size="body-l" weight="semibold">
                  {t(`guide--section-${section.id}--eyebrow`)}
                </Typography>
                <div className={styles.sectionTitle}>
                  <Typography type="h2" size="display-5">
                    {t(`guide--section-${section.id}--title`)}
                  </Typography>
                </div>
                <Typography type="p" size="body-xl">
                  {t(`guide--section-${section.id}--text`)}
                </Typography>
                <div className={styles.button}>
                  <Button
                    type="link"
                    href={section.link}
                    variant="tertiary"
                    size="medium"
                    fitWidth="m"
                    {...NEW_TAB_ATTRIBUTES}
                  >
                    <Typography
                      size="body-xl"
                      type="p"
                      weight="semibold"
                    >
                      {t(`guide--section-${section.id}--button`)}
                    </Typography>
                  </Button>
                </div>
              </div>
              <picture>
                <source
                  media={sharedMediaQueries.xl}
                  width={imageWidths.xl}
                  srcSet={buildUrl(section.id, 'xl')}
                />
                <source
                  media={sharedMediaQueries.l}
                  width={imageWidths.l}
                  srcSet={buildUrl(section.id, 'l')}
                />
                <source
                  media={sharedMediaQueries.m}
                  width={imageWidths.m}
                  srcSet={buildUrl(section.id, 'm')}
                />
                <img
                  srcSet={buildUrl(section.id, 's')}
                  alt={t(`"guide--section-${section.id}--altText`)}
                  width={imageWidths.s}
                  loading="lazy"
                  className={styles.image}
                />
              </picture>
            </section>
          ))}
        </div>
      </PitchPageLayout>
    </div>
  );
};

export default OwnerAndCaptainGuides;
